import * as React from 'react';
import { ArrowDown, ArrowUp, Check, Filter, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Campo } from '@/componentes/ui/primitivos';

/**
 * Grade com coluna clicável para ordenar e funil para filtrar por valores —
 * o autofiltro de planilha, que é o gesto que todo mundo já sabe fazer.
 *
 * Vive num componente só porque duas telas de conferência precisam
 * exatamente do mesmo comportamento (o Kardex e a fila de separação), e
 * comportamento duplicado diverge: uma ganha "marcar todos" e a outra não, uma
 * ordena empate de um jeito e a outra de outro.
 *
 * Tudo no cliente, sobre as linhas já carregadas. Reconsultar o servidor a
 * cada clique mudaria o conjunto debaixo de quem está conferindo — e conferir
 * exige que a lista fique parada.
 */

export interface ColunaGrade<T> {
  chave: string;
  rotulo: string;
  alinhar?: 'direita';
  /** Célula que quebra linha, para texto longo. */
  largura?: boolean;
  /** Valor de ordenação: número onde é número, texto onde é texto. */
  ordenar: (linha: T) => number | string;
  /** Texto do filtro por valor — e o que aparece na lista de opções. */
  texto: (linha: T) => string;
}

interface Ordem {
  chave: string;
  desc: boolean;
}

export interface Grade<T> {
  visiveis: T[];
  ordem: Ordem | null;
  temFiltro: boolean;
  /** `true` = sem ordenação nem filtro: a lista como veio. */
  intacta: boolean;
  limpar: () => void;
  alternarOrdem: (chave: string) => void;
  definirFiltro: (chave: string, conjunto: Set<string>) => void;
  filtroDe: (chave: string) => Set<string> | undefined;
  valoresDe: (chave: string) => string[];
  aberta: string | null;
  definirAberta: (chave: string | null) => void;
  colunas: readonly ColunaGrade<T>[];
}

/**
 * @param linhas conjunto completo já carregado
 * @param colunas definição das colunas
 * @param desempate ordem estável quando duas linhas empatam no critério
 */
export function useGrade<T>(
  linhas: readonly T[],
  colunas: readonly ColunaGrade<T>[],
  desempate: (a: T, b: T) => number,
): Grade<T> {
  const [ordem, definirOrdem] = React.useState<Ordem | null>(null);
  const [filtros, definirFiltros] = React.useState<Record<string, Set<string>>>({});
  const [aberta, definirAberta] = React.useState<string | null>(null);

  const temFiltro = React.useMemo(
    () => Object.values(filtros).some((c) => c.size > 0),
    [filtros],
  );

  const passa = React.useCallback(
    (l: T, ignorar?: string): boolean =>
      colunas.every((c) => {
        if (c.chave === ignorar) return true;
        const escolhidos = filtros[c.chave];
        return !escolhidos || escolhidos.size === 0 || escolhidos.has(c.texto(l));
      }),
    [colunas, filtros],
  );

  const visiveis = React.useMemo(() => {
    const filtradas = linhas.filter((l) => passa(l));
    if (ordem === null) return filtradas;
    const coluna = colunas.find((c) => c.chave === ordem.chave);
    if (!coluna) return filtradas;
    const sinal = ordem.desc ? -1 : 1;
    return [...filtradas].sort((a, b) => {
      const x = coluna.ordenar(a);
      const y = coluna.ordenar(b);
      const cmp =
        typeof x === 'number' && typeof y === 'number'
          ? x - y
          : String(x).localeCompare(String(y), 'pt-BR', { numeric: true });
      return cmp !== 0 ? cmp * sinal : desempate(a, b);
    });
  }, [linhas, ordem, passa, colunas, desempate]);

  /**
   * Valores oferecidos no filtro de uma coluna: os que existem nas linhas que
   * já passam pelos OUTROS filtros — igual ao Excel. Oferecer valor que outro
   * filtro já excluiu só produz lista vazia.
   */
  const valoresDe = React.useCallback(
    (chave: string): string[] => {
      const coluna = colunas.find((c) => c.chave === chave);
      if (!coluna) return [];
      const vistos = new Set<string>();
      for (const l of linhas) if (passa(l, chave)) vistos.add(coluna.texto(l));
      return [...vistos].sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }));
    },
    [linhas, colunas, passa],
  );

  return {
    visiveis,
    ordem,
    temFiltro,
    valoresDe,
    intacta: ordem === null && !temFiltro,
    colunas,
    aberta,
    definirAberta,
    limpar: () => {
      definirOrdem(null);
      definirFiltros({});
      definirAberta(null);
    },
    // Três estados por coluna: crescente, decrescente, de volta à ordem
    // original. O terceiro clique é o "desfazer" que as pessoas procuram.
    alternarOrdem: (chave) =>
      definirOrdem((o) =>
        o?.chave === chave ? (o.desc ? null : { chave, desc: true }) : { chave, desc: false },
      ),
    definirFiltro: (chave, conjunto) =>
      definirFiltros((f) => ({ ...f, [chave]: conjunto })),
    filtroDe: (chave) => filtros[chave],
  };
}

export function CabecalhoColuna<T>({
  coluna,
  grade,
}: {
  coluna: ColunaGrade<T>;
  grade: Grade<T>;
}): JSX.Element {
  const refFunil = React.useRef<HTMLButtonElement | null>(null);
  const ordenadaPor = grade.ordem?.chave === coluna.chave;
  const selecionados = grade.filtroDe(coluna.chave);
  const filtrada = selecionados !== undefined && selecionados.size > 0;
  const aberta = grade.aberta === coluna.chave;

  return (
    <th
      scope="col"
      className={cn(
        'sticky top-0 z-20 whitespace-nowrap border-b border-borda bg-superficie2 px-1.5 py-1',
        coluna.alinhar === 'direita' && 'text-right',
      )}
    >
      <span
        className={cn('flex items-center gap-0.5', coluna.alinhar === 'direita' && 'justify-end')}
      >
        <button
          type="button"
          onClick={() => grade.alternarOrdem(coluna.chave)}
          title={`Ordenar por ${coluna.rotulo}`}
          className={cn(
            'flex items-center gap-1 rounded px-1 py-0.5 text-[10px] font-bold uppercase tracking-wide transition-colors hover:bg-superficie hover:text-texto',
            ordenadaPor ? 'text-primaria' : 'text-textoFraco',
          )}
        >
          {coluna.rotulo}
          {ordenadaPor ? (
            grade.ordem?.desc ? (
              <ArrowDown className="h-3 w-3" />
            ) : (
              <ArrowUp className="h-3 w-3" />
            )
          ) : null}
        </button>

        <button
          ref={refFunil}
          type="button"
          onClick={() => grade.definirAberta(aberta ? null : coluna.chave)}
          title={`Filtrar ${coluna.rotulo}`}
          aria-label={`Filtrar ${coluna.rotulo}`}
          className={cn(
            'nao-imprimir rounded p-0.5 transition-colors hover:bg-superficie',
            filtrada ? 'text-primaria' : 'text-textoSuave hover:text-texto',
          )}
        >
          <Filter className={cn('h-3 w-3', filtrada && 'fill-current')} />
        </button>
      </span>

      {aberta ? (
        <PainelFiltro
          ancoragem={refFunil.current}
          rotulo={coluna.rotulo}
          valores={grade.valoresDe(coluna.chave)}
          selecionados={selecionados}
          aoAplicar={(c) => {
            grade.definirFiltro(coluna.chave, c);
            grade.definirAberta(null);
          }}
          aoFechar={() => grade.definirAberta(null)}
        />
      ) : null}
    </th>
  );
}

/**
 * Lista de valores com busca.
 *
 * `position: fixed` porque o cabeçalho é `sticky` dentro de um contêiner com
 * `overflow: auto` — no fluxo normal o painel seria recortado pela borda da
 * tabela e ficaria inalcançável.
 */
function PainelFiltro({
  ancoragem,
  rotulo,
  valores,
  selecionados,
  aoAplicar,
  aoFechar,
}: {
  ancoragem: HTMLElement | null;
  rotulo: string;
  valores: string[];
  selecionados: Set<string> | undefined;
  aoAplicar: (conjunto: Set<string>) => void;
  aoFechar: () => void;
}): JSX.Element {
  const [busca, definirBusca] = React.useState('');
  const [marcados, definirMarcados] = React.useState<Set<string>>(
    () => new Set(selecionados && selecionados.size > 0 ? selecionados : valores),
  );
  const caixa = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const foraDaCaixa = (e: MouseEvent): void => {
      if (!caixa.current?.contains(e.target as Node)) aoFechar();
    };
    const esc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') aoFechar();
    };
    document.addEventListener('mousedown', foraDaCaixa);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', foraDaCaixa);
      document.removeEventListener('keydown', esc);
    };
  }, [aoFechar]);

  const r = ancoragem?.getBoundingClientRect();
  const filtrados = valores.filter((v) => v.toLowerCase().includes(busca.trim().toLowerCase()));
  const todosMarcados = filtrados.length > 0 && filtrados.every((v) => marcados.has(v));

  return (
    <div
      ref={caixa}
      className="nao-imprimir fixed z-50 w-[260px] rounded-xl border border-borda bg-superficie p-2 text-left shadow-forte"
      style={{
        top: r ? Math.min(r.bottom + 6, window.innerHeight - 340) : 80,
        left: r ? Math.min(Math.max(8, r.left - 200), window.innerWidth - 276) : 80,
      }}
    >
      <p className="rotulo mb-1.5 px-1">Filtrar {rotulo}</p>

      <span className="relative mb-1.5 block">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-textoSuave" />
        <Campo
          value={busca}
          onChange={(e) => definirBusca(e.target.value)}
          placeholder="procurar valor"
          aria-label={`Procurar valor em ${rotulo}`}
          className="min-h-[30px] pl-7 text-[12px]"
        />
      </span>

      <button
        type="button"
        onClick={() =>
          definirMarcados((m) => {
            const novo = new Set(m);
            for (const v of filtrados) {
              if (todosMarcados) novo.delete(v);
              else novo.add(v);
            }
            return novo;
          })
        }
        className="mb-1 w-full rounded-md px-1.5 py-1 text-left text-[11.5px] font-semibold text-primaria hover:bg-primaria-suave"
      >
        {todosMarcados ? 'desmarcar todos' : 'marcar todos'} ({filtrados.length})
      </button>

      <div className="max-h-[190px] overflow-y-auto">
        {filtrados.map((v) => {
          const marcado = marcados.has(v);
          return (
            <button
              key={v}
              type="button"
              onClick={() =>
                definirMarcados((m) => {
                  const novo = new Set(m);
                  if (novo.has(v)) novo.delete(v);
                  else novo.add(v);
                  return novo;
                })
              }
              className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-[12px] hover:bg-superficie2"
            >
              <span
                className={cn(
                  'grid h-4 w-4 shrink-0 place-items-center rounded border',
                  marcado ? 'gradiente-marca border-transparent text-white' : 'border-bordaForte',
                )}
              >
                {marcado ? <Check className="h-3 w-3" /> : null}
              </span>
              <span className="truncate">{v}</span>
            </button>
          );
        })}
        {filtrados.length === 0 ? (
          <p className="px-1.5 py-3 text-center text-[11.5px] text-textoFraco">nada encontrado</p>
        ) : null}
      </div>

      <div className="mt-1.5 flex items-center gap-1.5 border-t border-borda pt-1.5">
        <Botao
          variante="primaria"
          tamanho="micro"
          onClick={() =>
            // Marcar tudo é o mesmo que não filtrar — guardar o conjunto
            // inteiro faria a coluna parecer filtrada sem estar.
            aoAplicar(marcados.size === valores.length ? new Set() : marcados)
          }
        >
          Aplicar
        </Botao>
        <Botao variante="fantasma" tamanho="micro" onClick={() => aoAplicar(new Set())}>
          Limpar
        </Botao>
        <span className="ml-auto text-[10.5px] text-textoFraco">
          {marcados.size}/{valores.length}
        </span>
      </div>
    </div>
  );
}
